import { useTranslation } from "react-i18next";
import { FilterableMultiSelect, Select, SelectItem } from "@carbon/react";
import OwnerFilter from "../../components/OwnerFilter/OwnerFilter";
import { MaterialType, TagResponse } from "../../types";
import "./materials.scss";

export interface MaterialListFiltersProps {
  /** Distinguishes element ids between screens that mount this component — Carbon's
   * `FilterableMultiSelect`/`Select` both require a real, unique `id`. */
  idPrefix: string;
  materialType: MaterialType;
  onMaterialTypeChange: (type: MaterialType) => void;
  availableTags: TagResponse[];
  selectedTags: TagResponse[];
  onTagsChange: (tags: TagResponse[]) => void;
  /** Owner (responsible-user) multiselect — the unified list defaults this to the current user. */
  selectedOwnerIds: string[];
  onOwnerChange: (ownerIds: string[]) => void;
}

/**
 * The type + tags filter row shared by `Materials.tsx` (`/materials`) and
 * `CompanyMaterials.tsx` (`/materials/company`) — both screens filtered by the same two facets
 * pre-Carbon (`MaterialType` toggle, `TagSelector`). Rebuilt on plain Carbon `Select`/
 * `FilterableMultiSelect` rather than the old Tailwind `<select>`/`TagSelector` pair, per the
 * brief's zero-Tailwind requirement for every rewritten Materials screen.
 */
const MaterialListFilters = ({
  idPrefix,
  materialType,
  onMaterialTypeChange,
  availableTags,
  selectedTags,
  onTagsChange,
  selectedOwnerIds,
  onOwnerChange,
}: MaterialListFiltersProps) => {
  const { t } = useTranslation();

  return (
    <div className="materials__filters">
      <OwnerFilter
        id={`${idPrefix}-owner`}
        selectedOwnerIds={selectedOwnerIds}
        onChange={onOwnerChange}
      />

      <Select
        id={`${idPrefix}-type`}
        labelText={t("materials.type")}
        value={materialType}
        onChange={(event) =>
          onMaterialTypeChange(event.target.value as MaterialType)
        }
        className="materials__type-select"
      >
        <SelectItem value={MaterialType.ALL} text={t("materialTypes.all")} />
        <SelectItem
          value={MaterialType.DIRECT_GOODS}
          text={t("materialTypes.pi")}
        />
        <SelectItem
          value={MaterialType.INDIRECT_GOODS}
          text={t("materialTypes.npi")}
        />
        <SelectItem
          value={MaterialType.SERVICES}
          text={t("materialTypes.services")}
        />
      </Select>

      {availableTags.length > 0 ? (
        <FilterableMultiSelect
          id={`${idPrefix}-tags`}
          className="materials__tag-select"
          titleText={t("materials.filterByTags")}
          placeholder={t("materials.filterByTags")}
          items={availableTags}
          itemToString={(tag) => tag?.name ?? ""}
          selectedItems={selectedTags}
          onChange={({ selectedItems }) => onTagsChange(selectedItems ?? [])}
        />
      ) : null}
    </div>
  );
};

export default MaterialListFilters;
